import { useEffect } from "react";

/**
 * Sets document title + meta description dynamically per page.
 * Non-invasive — restores nothing (each page sets its own values on mount).
 *
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with " · 1000 ARTS")
 * @param {string} props.description - Meta description (max ~160 chars recommended)
 * @param {string} [props.canonical] - Optional canonical URL (relative or absolute)
 */
export function useSEO({ title, description, canonical, jsonLd, robots }) {
  useEffect(() => {
    // Title
    if (title) document.title = title.includes("1000") ? title : `${title} · 1000 ARTS — École de danse Cugy VD`;

    // Meta description
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }

    // Canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      const url = canonical.startsWith("http") ? canonical : `https://1000arts.ch${canonical}`;
      link.setAttribute("href", url);

      // Version texte brut de cette page, destinée aux assistants IA (invisible pour les visiteurs).
      const p = canonical.startsWith("http") ? new URL(canonical).pathname : canonical;
      let alt = document.querySelector('link[data-seo-page-txt]');
      if (!alt) {
        alt = document.createElement("link");
        alt.setAttribute("rel", "alternate");
        alt.setAttribute("type", "text/plain");
        alt.setAttribute("data-seo-page-txt", "true");
        document.head.appendChild(alt);
      }
      alt.setAttribute("href", `https://1000arts.ch/txt${p === "/" ? "/index" : p.replace(/\/$/, "")}.txt`);
    }
  }, [title, description, canonical]);

  // Directive robots propre à la page (ex. un stage fermé ne doit pas être indexé).
  // La valeur précédente est restaurée au démontage.
  useEffect(() => {
    if (!robots) return undefined;
    let el = document.querySelector('meta[name="robots"]');
    const previous = el ? el.getAttribute("content") : null;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", robots);
    return () => {
      if (previous) el.setAttribute("content", previous);
      else el.remove();
    };
  }, [robots]);

  // Données structurées propres à la page (Course, Offer…). Le bloc DanceSchool global
  // reste dans index.html. On nettoie au démontage pour ne jamais laisser le schéma
  // d'une page sur une autre.
  useEffect(() => {
    if (!jsonLd) return undefined;
    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const nodes = items.filter(Boolean).map((data) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo-page", "true");
      el.textContent = JSON.stringify(data);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((n) => n.remove());
  }, [jsonLd]);
}
